import styled from "styled-components";
import { RiNextjsFill } from "react-icons/ri";
import {
  SiCss3,
  SiFirebase,
  SiHtml5,
  SiJavascript,
  SiReact,
  SiStyledcomponents,
  SiTypescript,
  SiGithub,
  SiTailwindcss,
  SiFigma,
  SiJest,
  SiRedux,
  SiTestinglibrary,
} from "react-icons/si";
import { FaGitAlt } from "react-icons/fa";
import { forwardRef, useState } from "react";

import Appear from "../animations/Appear";
import { FiFramer } from "react-icons/fi";

interface SkillsProps {
  onClick?: () => void;
}

interface Skill {
  id: number;
  icon: JSX.Element;
  title: string;
  description: string;
}

const skills: Skill[] = [
  {
    id: 1,
    icon: <SiHtml5 />,
    title: "HTML",
    description: "Crafting semantic and accessible web content.",
  },
  {
    id: 2,
    icon: <SiCss3 />,
    title: "CSS & Sass",
    description: "Creating responsive layouts and dynamic styles.",
  },
  {
    id: 3,
    icon: <SiTailwindcss />,
    title: "Tailwind CSS",
    description:
      "Designing and customizing responsive UIs with utility-first CSS classes.",
  },
  {
    id: 4,
    icon: <SiTypescript />,

    title: "JavaScript & TypeScript",
    description:
      "Building interactive applications with a focus on type safety.",
  },
  {
    id: 5,
    icon: <SiReact />,
    title: "React",
    description:
      "Developing component-based UIs and managing state effectively.",
  },
  {
    id: 6,
    icon: <SiRedux />,
    title: "Redux",
    description:
      "Managing application state with a predictable state container.",
  },
  {
    id: 7,
    icon: <RiNextjsFill />,
    title: "Next.js",
    description:
      "Implementing server-side rendering and static site generation.",
  },
  {
    id: 8,
    icon: <SiStyledcomponents />,
    title: "Styled Components",
    description: "Enhancing styling processes with CSS-in-JS.",
  },
  {
    id: 9,
    icon: <SiJest />,
    title: "Jest",
    description: "Writing unit tests and ensuring code reliability.",
  },
  {
    id: 10,
    icon: <SiTestinglibrary />,
    title: "React Testing Library",
    description:
      "Testing React components with a focus on user behavior and interaction.",
  },
  {
    id: 11,
    icon: <FiFramer />,
    title: "Framer Motion",
    description:
      "Creating smooth and interactive animations for React applications.",
  },
  {
    id: 12,
    icon: <SiFirebase />,
    title: "Firebase",
    description:
      "Utilizing Firebase for real-time databases, authentication, and hosting.",
  },
  {
    id: 13,
    icon: <SiGithub />,
    title: "GitHub",
    description:
      "Managing repositories, pull requests, and contributing to open-source projects.",
  },
  {
    id: 14,
    icon: <FaGitAlt />,
    title: "Git",
    description:
      "Using Git for version control, including branching, merging, and resolving conflicts.",
  },
  {
    id: 15,
    icon: <SiFigma />,
    title: "Figma",
    description:
      "Designing UIs, creating interactive prototypes, and collaborating in real-time.",
  },
];

const Skills = forwardRef<HTMLDivElement, SkillsProps>((props, ref) => {
  const [selectedSkillId, setSelectedSkillId] = useState<number | null>(null);

  const handleClick = (id: number) => {
    setSelectedSkillId(selectedSkillId === id ? null : id);
  };

  return (
    <AboutContainer ref={ref}>
      <Header>Skills</Header>

      <Appear>
        <SkillsGrid>
          {skills.map((skill) => (
            <SkillCard key={skill.id} onClick={() => handleClick(skill.id)}>
              <SkillIcon>{skill.icon}</SkillIcon>
              <SkillTitle>{skill.title}</SkillTitle>
              {selectedSkillId === skill.id && (
                <SkillDescription>{skill.description}</SkillDescription>
              )}
            </SkillCard>
          ))}
        </SkillsGrid>
      </Appear>
    </AboutContainer>
  );
});

export default Skills;

// Styled components

const AboutContainer = styled.div`
  width: 100%;
  margin-top: 40px;
  padding-left: 40px;
  padding-right: 40px;

  & > h3 {
    color: white;
    font-size: 2.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 5px;
    letter-spacing: 3px;
    font-style: oblique;

    @media (max-width: 460px) {
      margin-top: 10px;
    }
  }
`;

const Header = styled.h3`
  color: white;
  font-size: 2.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 5px;
  letter-spacing: 3px;
  font-style: oblique;

  @media (max-width: 460px) {
    margin-top: 10px;
  }
`;

const SkillsGrid = styled.div`
  display: grid;

  gap: 16px;
  padding: 5px 10px;
  align-items: start;
  grid-template-columns: repeat(3, 1fr); /* 3 tiles per row */

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr); /* 2 tiles per row */
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(1, 1fr); /* 1 tile per row */
  }
`;

const SkillCard = styled.div`
  background: rgba(185, 185, 185, 0.54);
  border-radius: 8px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.5);
  padding-top: 10px;
  padding-right: 5px;
  padding-left: 5px;
  padding-bottom: 10px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;
  &:hover {
    background: rgba(36, 36, 41, 0.67);
    transform: scale(1.05);
    color: white;
  }
`;

const SkillIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 10px;
  @media (max-width: 460px) {
    font-size: 1.4rem;
  }
`;

const SkillTitle = styled.div`
  font-size: 1.25rem;
  margin-bottom: 10px;
  margin-bottom: 10px;
  @media (max-width: 460px) {
    font-size: 1rem;
  }
`;

const SkillDescription = styled.div`
  font-size: 0.875rem;
  color: white;
  margin-top: 10px;
  padding-right: 20px;
  padding-left: 20px;
  max-width: 200px;
  margin-bottom: 10px;
  @media (max-width: 460px) {
    font-size: 0.7rem;
  }
`;
